import React, { useEffect, useState } from "react";
import KeywordSuggester from "components/AIGenerators/KeyWordSuggester";
import SectionTitleAndDescription from "./SectionTitleAndDescription";
import Checkbox from "components/Common/Checkbox/Checkbox";
import SearchFilterDropdown, { OptionType } from "components/Common/SearchableDropdown/SearchFilterDropdown";
import { useDispatch, useSelector } from "react-redux";
import { useAppSelector } from "hooks/reduxHooks";
import { RootState } from "store/store";
import { IKeywordOption } from "./AddKeywords";
import {
  addJobTitle,
  removeJobTitle,
  setExactJobTitleMatch,
  setOnlyGreenHouseCandidate,
  setTitleWithPreviousJob,
  removeAllJobTitle,
  setOnlyLeverCandidate,
} from "store/slices/searcSlice";
import { getJobTitleResult } from "api/search.api";
import { useDebounce } from "utils";
import TitleSuggester from "components/AIGenerators/TitleSuggester";
import SearchableMultiJobTitle from "components/Common/SearchableDropdown/SeachableMultiJobTitle";

interface AddJobTitleProps {
  setJobTitleValue: React.Dispatch<React.SetStateAction<string[]>>;
  isModal?: boolean;
  searchId?: string | null;
}

const AddJobTitle: React.FC<AddJobTitleProps> = ({ setJobTitleValue, isModal, searchId }) => {
  const [AddJobTitle, setAddJobTitle] = useState<string[]>([]);
  const [allJobTitles, setAllJobTitles] = useState<OptionType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userData = useAppSelector((state) => state.auth.user);

  const dispatch = useDispatch();

  const selectedProject = useAppSelector((state: any) => state.search?.selectedProject);

  const selectors = useSelector((state: RootState) => ({
    allAddJobTitle: state.search.job_titles,
    jobTitleExactmatch: state.search.exact_job_title_match,
    OnlyGreenHouseCandidate: state.search.search_only_greenhouse_mckinney,
    OnlyLeverCandidate: state.search.search_only_lever_ss,
    previousJobTitles: state.search.allow_previous_job_titles,
  }));

  useEffect(() => {
    const filteredJobTitles = selectors.allAddJobTitle.filter((item: any) => item !== undefined && item !== null);
    setAddJobTitle(filteredJobTitles);
    setJobTitleValue(filteredJobTitles);
    console.log("All add job titles:", selectors.allAddJobTitle);
  }, [selectors.allAddJobTitle]);

  const fetchJobTitles = async (query: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getJobTitleResult(query);
      setAllJobTitles(response.map((title: string) => ({ value: title, label: title })));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (selected: IKeywordOption[] | null) => {
    const selectedValues = selected ? selected.map((option) => option.value) : [];
    setAddJobTitle([...AddJobTitle, selectedValues[selectedValues.length - 1]]);
    setJobTitleValue([...AddJobTitle, selectedValues[selectedValues.length - 1]]);
    dispatch(addJobTitle(selectedValues[selectedValues.length - 1]));
  };

  const onChange = (values: string[]) => {
    const uniqueKeywords: any = values.filter((value: any) => !selectors.allAddJobTitle.includes(value.value));
    if (uniqueKeywords.length > 0) {
      setAddJobTitle((prev) => [...prev, uniqueKeywords[0].value]);
      setJobTitleValue((prev) => [...prev, uniqueKeywords[0].value]);
      dispatch(addJobTitle(uniqueKeywords[0].value));
    }
  };

  const handleDeselect = (value: string | null) => {
    console.log("JobTitleRemoved : ", value);
    if (value) dispatch(removeJobTitle(value));
  };

  const handleAdd = (skill: string) => {
    dispatch(addJobTitle(skill));
  };

  const debouncedSearchSkill = useDebounce(fetchJobTitles, 300);

  const handleInputChange = (inputValue: string) => {
    debouncedSearchSkill(inputValue);
  };

  // useEffect(() => {
  //   if (selectedProject && !isModal && !searchId) {
  //     console.log("Project 77 : ", selectedProject);
  //     dispatch(removeAllJobTitle([]));
  //     try {
  //       const job_titles = JSON.parse(selectedProject.official_title);
  //       console.log("Job Titles 80 : ", job_titles);
  //       setAddJobTitle(job_titles);
  //       setJobTitleValue(job_titles);
  //       job_titles.map((item: string, index: number) => {
  //         handleAdd(item);
  //       });
  //     } catch (error) {
  //       console.log("Job Titles 86 : ", selectedProject.official_title);
  //       setAddJobTitle([selectedProject.official_title]);
  //       setJobTitleValue([selectedProject.official_title]);
  //       handleAdd(selectedProject.official_title);
  //     }
  //   }
  // }, [selectedProject]);

  const isGreenHouse = userData?.plugins.some((plugin: any) => plugin.name === "Greenhouse-Mckinney");
  const isSmartRecruiter = userData?.plugins.some((plugin: any) => plugin.name === "SmartRecruiter");
  const isLever = userData?.plugins.some((plugin: any) => plugin.name === "Lever-SS");

  const checkboxOptions = [
    {
      label: "Exact match",
      checked: selectors.jobTitleExactmatch,
      action: () => dispatch(setExactJobTitleMatch(!selectors.jobTitleExactmatch))
    },
    {
      label: "Show candidates that have previously held that title",
      checked: selectors.previousJobTitles,
      action: () => dispatch(setTitleWithPreviousJob(!selectors.previousJobTitles)),
      description: "Show candidates that have previously held that title"
    },
    {
      label: "Search only in Greenhouse",
      checked: selectors.OnlyGreenHouseCandidate,
      isDisabled: !isGreenHouse,
      action: () => dispatch(setOnlyGreenHouseCandidate(!selectors.OnlyGreenHouseCandidate))
    },
    {
      label: "Search only in Lever",
      checked: selectors.OnlyLeverCandidate,
      isDisabled: !isLever,
      action: () => dispatch(setOnlyLeverCandidate(!selectors.OnlyLeverCandidate))
    },
  ];

  const handleClearAll = () => {
    console.log("Clear All clicked!");
    dispatch(removeAllJobTitle([]));
    setAddJobTitle([]);
    setJobTitleValue([]);
  };

  return (
    <>
      <div className="w-1/4">
        <SectionTitleAndDescription
          title="Get AI suggestions"
          isRequiredSection={false}
          isHelpInfoReq={false}
          description="Get our AI suggestions."
        />
      </div>

      <div className="w-3/4">
        <TitleSuggester
          jobTitles={selectors.allAddJobTitle}
          isSectionTitleRequired={true}
          sectionTitle="Recommended"
          handleAdd={handleAdd}
          selectedValues={selectors.allAddJobTitle}
          onClearAll={handleClearAll}
        />
        <SearchableMultiJobTitle
          mode="tags"
          placeholder="Position"
          defaultValue={AddJobTitle}
          onChange={onChange}
          handleDeselect={handleDeselect}
        />
        {/* <SearchFilterDropdown
          mode="multiple"
          options={allJobTitles}
          onChange={(values: string[]) => handleChange(values.map((value) => ({ value, label: value })))}
          defaultValue={AddJobTitle}
          handleDeselect={handleDeselect}
          maxSelect={10}
          placeholder="Enter and select job titles"
          onInputChange={handleInputChange}
        /> */}

        <div className="flex gap-4 justify-start items-center mt-6 flex-wrap">
          {checkboxOptions.filter((val: any) => val.isDisabled !== true)
            .map(({ label, checked, action }, index) => (
              <div key={index} className="checkbox-wrapper flex flex-wrap justify-start items-center gap-2">
                <Checkbox
                  inputName={label}
                  inputValue={label}
                  inputId={label}
                  labelFor={label}
                  labelTitle={label}
                  checked={checked}
                  onChange={action}
                />
              </div>
            ))}
        </div>
      </div>
    </>
  );
};

export default AddJobTitle;
